import { useMemo } from "react"
import { create } from "zustand"

import { isProfessionalUser } from "~/common/auth/user"
import {
  getVideoGenerationPreferenceFromBehavior,
  type VideoGenerationPreference,
} from "~/common/video/videoGenerationPreference"
import {
  DEFAULT_VIDEO_OPTIONS,
  getSafeVideoOptions,
  type VideoOptions,
} from "~/common/constants"
import {
  getCurrentUser,
  getUserBehavior,
  loginWithXqt,
  logoutUser,
  sendLoginCode,
  verifyLoginCode,
} from "~/api/user"

type SessionState = {
  initialized: boolean
  initializing: boolean
  isNewUser?: boolean
  isProfessional: boolean
  loginModalOpen: boolean
  user?: API.User.SessionUser
  videoGenerationPreference?: VideoGenerationPreference
  clear: () => void
  closeLoginModal: () => void
  init: () => Promise<void>
  login: (
    params: API.User.VerifyCodeParams,
  ) => Promise<API.User.SessionUser | undefined>
  loginWithXqt: (
    params: API.User.XqtLoginParams,
  ) => Promise<API.User.SessionUser | undefined>
  logout: () => Promise<void>
  openLoginModal: () => void
  refreshPointBalance: () => Promise<API.User.SessionUser | undefined>
  refreshUser: () => Promise<API.User.SessionUser | undefined>
  sendLoginCode: (params: API.User.GetVerifyCodeParams) => Promise<void>
  setUser: (user: API.User.SessionUser | undefined) => void
}

export const useSessionStore = create<SessionState>((set, get) => ({
  initialized: false,
  initializing: false,
  isNewUser: undefined,
  isProfessional: false,
  loginModalOpen: false,
  user: undefined,
  videoGenerationPreference: undefined,
  clear: () => {
    set({
      isNewUser: undefined,
      isProfessional: false,
      user: undefined,
      videoGenerationPreference: undefined,
    })
  },

  closeLoginModal: () => {
    set({ loginModalOpen: false })
  },

  init: async () => {
    if (get().initializing) return

    set({ initializing: true })

    try {
      await get().refreshUser()
    } catch {
      get().clear()
    } finally {
      set({
        initialized: true,

        initializing: false,
      })
    }
  },

  login: async (params) => {
    const response = await verifyLoginCode(params)

    const user = response.data || undefined

    set({
      isNewUser: undefined,
      isProfessional: isProfessionalUser(user),
      user,
      videoGenerationPreference: undefined,
    })

    set({ loginModalOpen: false })

    return get()
      .refreshUser()

      .catch(() => user)
  },

  loginWithXqt: async (params) => {
    const response = await loginWithXqt(params)
    const user = response.data || undefined

    set({
      isNewUser: undefined,
      isProfessional: isProfessionalUser(user),
      user,
      videoGenerationPreference: undefined,
    })

    return get()
      .refreshUser()
      .catch(() => user)
  },

  logout: async () => {
    await logoutUser()

    get().clear()
  },

  openLoginModal: () => {
    set({ loginModalOpen: true })
  },

  refreshPointBalance: async () => {
    const response = await getCurrentUser()
    const user = response.data || undefined

    if (!user) {
      get().clear()
      return undefined
    }

    set({
      isProfessional: isProfessionalUser(user),
      user,
    })

    return user
  },

  refreshUser: async () => {
    const [response, userBehaviorResponse] = await Promise.all([
      getCurrentUser(),
      getUserBehavior().catch(() => undefined),
    ])

    const user = response.data || undefined
    const behavior = userBehaviorResponse?.data
    const preference = user
      ? getVideoGenerationPreferenceFromBehavior(behavior)
      : undefined

    set({
      isNewUser: user
        ? typeof behavior?.video_count === "number"
          ? behavior.video_count === 0
          : (get().isNewUser ?? false)
        : false,
      isProfessional: isProfessionalUser(user),
      user,
      videoGenerationPreference: user
        ? (preference ?? get().videoGenerationPreference)
        : undefined,
    })

    return user
  },

  sendLoginCode: async (params) => {
    await sendLoginCode(params)
  },

  setUser: (user) => {
    set({
      isNewUser: undefined,
      isProfessional: isProfessionalUser(user),
      user,
      videoGenerationPreference: undefined,
    })
  },
}))

// 新建任务时的默认生成参数：用户历史偏好覆盖兜底值，并按模型能力修正
export const useDefaultVideoOptions = (
  fallbackOptions: VideoOptions = DEFAULT_VIDEO_OPTIONS,
) => {
  const isNewUser = useSessionStore((state) => state.isNewUser)
  const preference = useSessionStore((state) => state.videoGenerationPreference)
  const videoOptions = useMemo(() => {
    return getSafeVideoOptions({ ...fallbackOptions, ...preference })
  }, [fallbackOptions, preference])

  return {
    isNewUser: isNewUser ?? false,
    videoOptions,
  }
}
